/**
 * Formateadores de datos
 * Monedero Digital NFC
 */

import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { CURRENCY_CONFIG } from './constants';

/**
 * Formatea un monto de dinero
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return `${CURRENCY_CONFIG.SYMBOL}0`;
  
  return new Intl.NumberFormat(CURRENCY_CONFIG.LOCALE, {
    style: 'currency',
    currency: CURRENCY_CONFIG.CODE,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formatea un número sin símbolo de moneda
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined) return '0';
  
  return new Intl.NumberFormat(CURRENCY_CONFIG.LOCALE).format(number);
};

/**
 * Formatea una fecha de manera legible
 */
export const formatDate = (date, formatString = 'dd/MM/yyyy') => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatString, { locale: es });
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return '';
  }
};

/**
 * Formatea fecha y hora
 */
export const formatDateTime = (date) => {
  return formatDate(date, 'dd/MM/yyyy HH:mm');
};

/**
 * Formatea solo la hora
 */
export const formatTime = (date) => {
  return formatDate(date, 'HH:mm:ss');
};

/**
 * Formatea una fecha relativa (ej: "hace 2 horas")
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: es });
  } catch (error) {
    console.error('Error formateando fecha relativa:', error);
    return '';
  }
};

/**
 * Formatea un serial number de NFC de manera legible
 */
export const formatNFCSerial = (serialNumber) => {
  if (!serialNumber) return 'N/A';
  return serialNumber.toUpperCase();
};

/**
 * Trunca un texto largo
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitaliza la primera letra de cada palabra
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Capitaliza solo la primera letra
 */
export const capitalizeFirst = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Formatea un nombre completo (limpia espacios extra)
 */
export const formatName = (name) => {
  if (!name) return '';
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(word => capitalizeFirst(word))
    .join(' ');
};

/**
 * Formatea un tipo de transacción para mostrar
 */
export const formatTransactionType = (type) => {
  const types = {
    pago: 'Pago',
    recarga: 'Recarga',
  };
  return types[type] || type;
};

/**
 * Formatea una categoría de producto
 */
export const formatCategory = (category) => {
  const categories = {
    comida: 'Comida',
    bebidas: 'Bebidas',
    snacks: 'Snacks',
    papeleria: 'Papelería',
    otros: 'Otros',
  };
  return categories[category] || capitalizeFirst(category);
};

/**
 * Formatea un rol de usuario
 */
export const formatRole = (role) => {
  const roles = {
    vendedor: 'Vendedor',
    administrador: 'Administrador',
    estudiante: 'Estudiante',
  };
  return roles[role] || capitalizeFirst(role);
};

/**
 * Formatea un porcentaje
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Formatea bytes a tamaño legible
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Quita el formato de moneda y devuelve el número
 */
export const parseCurrency = (formattedAmount) => {
  if (typeof formattedAmount === 'number') return formattedAmount;
  if (!formattedAmount) return 0;
  
  // Remover símbolo de moneda, puntos y comas
  const cleaned = formattedAmount
    .replace(/[^0-9,-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  
  return parseFloat(cleaned) || 0;
};

/**
 * Formatea un ID de transacción de manera corta
 */
export const formatTransactionId = (id) => {
  if (!id) return 'N/A';
  // Mostrar solo los últimos 8 caracteres
  return id.length > 8 ? `...${id.slice(-8)}` : id;
};

export default {
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateTime,
  formatTime,
  formatRelativeTime,
  formatNFCSerial,
  truncateText,
  capitalizeWords,
  capitalizeFirst,
  formatName,
  formatTransactionType,
  formatCategory,
  formatRole,
  formatPercentage,
  formatBytes,
  parseCurrency,
  formatTransactionId,
};
